<template>
  <div ref="triangleEl" :style="triangleStyle" class="triangle-wrapper">
    <svg
      :width="svgW"
      :height="svgH"
      overflow="visible"
      :viewBox="`0 0 ${svgW} ${svgH}`"
      style="position: absolute; top: 0; left: 0"
    >
      <!-- hlavný trojuholník -->
      <polygon :points="trianglePoints" fill="none" stroke="black" stroke-width="2" />
      <!-- bleed outline (vonkajšia, dashed) -->
      <polygon
        v-if="bleedPx > 0"
        :points="bleedOutlinePoints"
        fill="none"
        stroke="#F79009"
        stroke-width="2"
        stroke-dasharray="6,4"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, defineExpose, defineProps, defineEmits, watchEffect, type CSSProperties } from 'vue'
import { useCanvasUnits } from '@/composables/useCanvasUnits.ts'
import {useDesignerStore} from "@/stores/designerStore.ts";

const props = defineProps<{
  width: number
  height: number
  positionX: number
  positionY: number
  zIndex?: number
  bleedOffset?: number
}>()

const emit = defineEmits([
  'update:svg',
  'update:width',
  'update:height',
  'update:positionX',
  'update:positionY',
])

const { mmToPx } = useCanvasUnits()
const designerStore = useDesignerStore()
const triangleEl = ref<HTMLElement | null>(null)

const wPx = computed(() => mmToPx(props.width, 'x'))
const hPx = computed(() => mmToPx(props.height, 'y'))
const bleedPx = computed(() => mmToPx(props.bleedOffset || 0, 'x'))

const svgW = computed(() => wPx.value + bleedPx.value * 2)
const svgH = computed(() => hPx.value + bleedPx.value * 2)

const triStyle = computed<Partial<CSSProperties>>(() => {
  void useDesignerStore().scaleTick;
  return {
    position: 'absolute',
      left: `${mmToPx(props.positionX, 'x') - bleedPx.value}px`,
    top: `${mmToPx(props.positionY, 'y') - bleedPx.value}px`,   // 👈 FIX: 'y'
    width: `${svgW.value}px`,
    height: `${svgH.value}px`,
    pointerEvents: 'none',
    zIndex: `${props.zIndex ?? 10}`,
  }
})

const A = computed(() => ({ x: bleedPx.value + wPx.value / 2, y: bleedPx.value + 0 }))
const B = computed(() => ({ x: bleedPx.value + wPx.value,   y: bleedPx.value + hPx.value }))
const C = computed(() => ({ x: bleedPx.value + 0,           y: bleedPx.value + hPx.value }))

const trianglePoints = computed(() =>
  [A.value, B.value, C.value].map((p) => `${p.x},${p.y}`).join(' ')
)

const center = computed(() => ({
  x: (A.value.x + B.value.x + C.value.x) / 3,
  y: (A.value.y + B.value.y + C.value.y) / 3,
}))

function offsetEdge(p1: { x: number; y: number }, p2: { x: number; y: number }, o: number) {
  const dx = p2.x - p1.x, dy = p2.y - p1.y
  let nx = dy, ny = -dx
  const midx = (p1.x + p2.x) / 2 - center.value.x
  const midy = (p1.y + p2.y) / 2 - center.value.y
  if (nx * midx + ny * midy < 0) { nx = -nx; ny = -ny }
  const len = Math.hypot(nx, ny) || 1
  nx /= len; ny /= len
  return { p1: { x: p1.x + nx * o, y: p1.y + ny * o }, p2: { x: p2.x + nx * o, y: p2.y + ny * o } }
}

const edges = computed(() => {
  const o = bleedPx.value
  return [
    offsetEdge(A.value, B.value, o),
    offsetEdge(B.value, C.value, o),
    offsetEdge(C.value, A.value, o),
  ]
})

function intersect(l1: any, l2: any) {
  const { p1, p2 } = l1, { p1: q1, p2: q2 } = l2
  const A1 = p2.y - p1.y, B1 = p1.x - p2.x, C1 = A1 * p1.x + B1 * p1.y
  const A2 = q2.y - q1.y, B2 = q1.x - q2.x, C2 = A2 * q1.x + B2 * q1.y
  const det = A1 * B2 - A2 * B1
  if (Math.abs(det) < 1e-6) return p1
  return { x: (B2 * C1 - B1 * C2) / det, y: (A1 * C2 - A2 * C1) / det }
}

const bleedOutlinePoints = computed(() => {
  const [eAB, eBC, eCA] = edges.value
  const P = intersect(eCA, eAB)
  const Q = intersect(eAB, eBC)
  const R = intersect(eBC, eCA)
  return [P, Q, R].map((p) => `${p.x},${p.y}`).join(' ')
})

function getSvg() {
  const w = props.width, h = props.height
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}mm" height="${h}mm" viewBox="0 0 ${w} ${h}">
    <polygon points="${w / 2},0 ${w},${h} 0,${h}" fill="none" stroke="black" stroke-width="0.5"/>
  </svg>`
}

defineExpose({ triangleEl, getSvg })
const triangleStyle = triStyle

// 👇 centruj a posielaj svg hore pri každej zmene
watchEffect(() => {
  // keď práve hydratujeme zo servera, nič neprepočítavaj
  if (designerStore.hydrating) {
    emit('update:svg', getSvg())
    return
  }

  const MAX_SHORT = 300
  const MAX_LONG = 400

  let newW = props.width
  let newH = props.height

  const isWidthShorter = newW <= newH

  // Kratší rozmer nesmie presiahnuť 300
  if (isWidthShorter && newW > MAX_SHORT) {
    newW = MAX_SHORT
    newH = Math.min(newH, MAX_SHORT)
  } else if (!isWidthShorter && newH > MAX_SHORT) {
    newH = MAX_SHORT
    newW = Math.min(newW, MAX_SHORT)
  }

  // Dlhší rozmer nesmie presiahnuť 400
  if (isWidthShorter && newH > MAX_LONG) {
    newH = MAX_SHORT
  } else if (!isWidthShorter && newW > MAX_LONG) {
    newW = MAX_SHORT
  }

  // Emituj zmeny len ak je potrebné
  if (newW !== props.width) emit('update:width', newW)
  if (newH !== props.height) emit('update:height', newH)

  // Centrovanie
  const canvasW = designerStore.canvasSizeInMm.width
  const canvasH = designerStore.canvasSizeInMm.height
  emit('update:positionX', +(canvasW / 2 - newW / 2).toFixed(2))
  emit('update:positionY', +(canvasH / 2 - newH / 2).toFixed(2))
  emit('update:svg', getSvg())
})
</script>

<style scoped>
.triangle-wrapper {
  /* nemusí mať overflow, celý bleed je už vo vnútri */
}
</style>
